<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'settings.tasks',
    path: 'sync-jobs',
    component: 'Tasks',
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'Tasks',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));

  const $data = computed(() => ({
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {},
    ux: {},
    dataRefs: {
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_lkqxubodb_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };

  const act_7315092382398562_closeModal_lkqxubodb =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_lkqxubodb_getActionArgs,
    });
  const inst_evh_7315092382398562_close_84czss77k =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_lkqxubodb,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_84czss77k],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_aip47q1dc =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_m3ml8m8h2 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_aip47q1dc,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_m3ml8m8h2],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_close_84czss77k,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_m3ml8m8h2,
    inst_evh_7315092382398562_reload,
  };

  const evh_7315092382398562_close_84czss77k = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_84czss77k.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_m3ml8m8h2 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_m3ml8m8h2.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="Tasks"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <div class="overflow-auto px-3">
      <h3 class="m-3">
        {{ gettext('Tasks') }}
      </h3>
      <div class="card my-3">
        <div class="card-body">
          <h5>
            {{ gettext('Expired contracts') }}
          </h5>
          <p class="card-text">
            {{
              gettext(
                'This task searches for contracts expired within X days and creates notifications if founds any.'
              )
            }}
          </p>
          <JobHandlerControl
            jobType="contracts:check_expiring_contracts"
            load-current
            :payload="() => ({})"
            :text="pgettext('Tasks|Command', 'Check expiring contracts')"
            icon="mdi:cog"
          >
          </JobHandlerControl>
        </div>
      </div>
      <div class="card my-3">
        <div class="card-body">
          <h5>
            {{ gettext('Inflation indexed fees') }}
          </h5>
          <p class="card-text">
            {{
              gettext(
                'This task searches for fees subject to inflation indexed price change and creates notifications if founds any.'
              )
            }}
          </p>
          <JobHandlerControl
            jobType="contracts:inflation_indexing"
            load-current
            :payload="() => ({})"
            :text="pgettext('Tasks|Command', 'Check inflation indexed fees.')"
            icon="mdi:cog"
          >
          </JobHandlerControl>
        </div>
      </div>
      <NAVOnlineInvoiceInterfacePanel> </NAVOnlineInvoiceInterfacePanel>
    </div>
  </ItpPage>
</template>
